import { useState, useEffect } from 'react'
import { apiService } from '../../services/api'
import { parseXlsxBuffer } from '../../utils/xlsx-parser'

interface ImportResult {
  message: string
  totalRows: number
  imported: number
  updated: number
  skipped: number
  failed: number
  errors?: string[]
}

interface InviteResult {
  message: string
  totalUsers: number
  cognitoCreated: number
  invitesSent: number
  invitesFailed: number
  errors?: string[]
}

const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

export default function XLSXImport() {
  const [xlsxFile, setXlsxFile] = useState<File | null>(null)
  const [competitionYear, setCompetitionYear] = useState(new Date().getFullYear().toString())
  const [loading, setLoading] = useState(false)
  const [sendingInvites, setSendingInvites] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null)
  const [fileSizeWarning, setFileSizeWarning] = useState(false)

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setFileSizeWarning(false)
    if (!file) { setXlsxFile(null); return }
    if (!file.name.endsWith('.xlsx')) {
      setToast({ message: 'Please select a valid XLSX file', type: 'error' })
      setXlsxFile(null)
      return
    }
    if (file.size > MAX_FILE_SIZE_BYTES) setFileSizeWarning(true)
    setXlsxFile(file)
    setImportResult(null)
    setInviteResult(null)
  }

  const handleImport = async () => {
    if (!xlsxFile) { setToast({ message: 'Please select an XLSX file', type: 'error' }); return }
    if (!competitionYear.trim()) { setToast({ message: 'Please enter a competition year', type: 'error' }); return }

    setLoading(true)
    setImportResult(null)
    setInviteResult(null)
    try {
      const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as ArrayBuffer)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsArrayBuffer(xlsxFile)
      })

      // Parse XLSX client-side
      const parseResult = parseXlsxBuffer(buffer)
      if (parseResult.errors.length > 0) {
        setToast({ message: parseResult.errors[0], type: 'error' })
        setLoading(false)
        return
      }
      if (parseResult.rows.length === 0 && parseResult.skippedRows === 0) {
        setToast({ message: 'XLSX file contains no pilot data', type: 'error' })
        setLoading(false)
        return
      }

      // Send parsed rows to backend
      const response = await apiService.importUsers(
        parseResult.rows as unknown as Record<string, string>[],
        competitionYear,
        parseResult.totalDataRows,
        parseResult.skippedRows
      )
      if (response.error) { setToast({ message: response.error.message, type: 'error' }); setLoading(false); return }
      if (response.data) {
        const data = response.data as unknown as ImportResult
        setImportResult(data)
        setToast({ message: data.message || 'Import completed successfully', type: 'success' })
        setXlsxFile(null)
        setFileSizeWarning(false)
        const fileInput = document.getElementById('xlsx-file-input') as HTMLInputElement
        if (fileInput) fileInput.value = ''
      }
    } catch (error) {
      setToast({ message: error instanceof Error ? error.message : 'Failed to read XLSX file', type: 'error' })
    }
    setLoading(false)
  }

  const handleSendAllInvites = async () => {
    setSendingInvites(true)
    setInviteResult(null)
    try {
      const response = await apiService.sendAllInvites(competitionYear)
      if (response.error) { setToast({ message: response.error.message, type: 'error' }); setSendingInvites(false); return }
      if (response.data) {
        const data = response.data as unknown as InviteResult
        setInviteResult(data)
        setToast({ message: data.message || 'Invites sent successfully', type: 'success' })
      }
    } catch (error) {
      setToast({ message: error instanceof Error ? error.message : 'Failed to send invites', type: 'error' })
    }
    setSendingInvites(false)
  }

  const isImportDisabled = !xlsxFile || !competitionYear.trim() || loading

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Pilots from XLSX</h2>
        <p className="text-gray-600">
          Import pilot data from an Airtribune XLSX export. Existing pilots (matched by email and competition year) will be updated; new pilots will be created.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <label htmlFor="competition-year" className="block text-sm font-medium text-gray-700 mb-2">Competition Year</label>
          <input id="competition-year" type="text" value={competitionYear}
            onChange={(e) => setCompetitionYear(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="2026" />
        </div>

        <div>
          <label htmlFor="xlsx-file-input" className="block text-sm font-medium text-gray-700 mb-2">XLSX File</label>
          <input id="xlsx-file-input" type="file" accept=".xlsx" onChange={handleFileChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          {xlsxFile && <p className="mt-2 text-sm text-gray-600">Selected: {xlsxFile.name} ({(xlsxFile.size / 1024).toFixed(2)} KB)</p>}
          {fileSizeWarning && <p className="mt-2 text-sm text-amber-600 font-medium">⚠ File exceeds {MAX_FILE_SIZE_MB} MB. Large files may take longer to process.</p>}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">XLSX Format Requirements</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>File must be an Airtribune XLSX export with 46 expected columns</li>
            <li>First row must contain column headers</li>
            <li>Only rows with status "Confirmed" or "Team pilot" will be imported</li>
            <li>Existing pilots are matched by email and competition year</li>
            <li>After import, use "Send All Invites" to create accounts and send login emails</li>
          </ul>
        </div>

        <button onClick={handleImport} disabled={isImportDisabled}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium">
          {loading ? 'Importing...' : 'Import Pilots'}
        </button>
      </div>

      {importResult && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Import Results</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total rows processed:</span>
              <span className="font-medium">{importResult.totalRows}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Imported (new):</span>
              <span className="font-medium text-green-600">{importResult.imported}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">Updated (existing):</span>
              <span className="font-medium text-blue-600">{importResult.updated}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Skipped (filtered):</span>
              <span className="font-medium text-gray-500">{importResult.skipped}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-600">Failed:</span>
              <span className="font-medium text-red-600">{importResult.failed}</span>
            </div>
          </div>

          {importResult.failed > 0 && importResult.errors && importResult.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Error Details:</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <ul className="text-sm text-red-800 space-y-1">
                  {importResult.errors.map((error: string, index: number) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                Pilots have been saved to the database. Click below to create Cognito accounts and send invite emails to all pilots.
              </p>
            </div>
            <button onClick={handleSendAllInvites} disabled={sendingInvites}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium">
              {sendingInvites ? 'Sending Invites...' : 'Send All Invites'}
            </button>
          </div>
        </div>
      )}

      {inviteResult && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Invite Results</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total users processed:</span>
              <span className="font-medium">{inviteResult.totalUsers}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Cognito accounts created:</span>
              <span className="font-medium">{inviteResult.cognitoCreated}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Invite emails sent:</span>
              <span className="font-medium text-green-600">{inviteResult.invitesSent}</span>
            </div>
            {inviteResult.invitesFailed > 0 && (
              <div className="flex justify-between">
                <span className="text-red-600">Invites failed:</span>
                <span className="font-medium text-red-600">{inviteResult.invitesFailed}</span>
              </div>
            )}
          </div>
          {inviteResult.errors && inviteResult.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Error Details:</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <ul className="text-sm text-red-800 space-y-1">
                  {inviteResult.errors.map((error: string, index: number) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
